import { WHATSAPP_CONFIG } from '../../config/whatsapp';
import type { WhatsAppMessage, WhatsAppResponse, WhatsAppGroup } from './types';
import { toCanonicalPhone } from '../../lib/phone';

export class WhatsAppAPI {
  private static headers = {
    'Authorization': `Bearer ${WHATSAPP_CONFIG.TOKEN}`,
    'Content-Type': 'application/json'
  };

  static async sendMessage({ groupId, text, previewUrl = true }: WhatsAppMessage): Promise<WhatsAppResponse> {
    // Validate required parameters
    if (!groupId?.trim()) {
      throw new Error('WhatsApp API Error: groupId is required');
    }
    if (!text?.trim()) {
      throw new Error('WhatsApp API Error: text message is required');
    }
    if (!WHATSAPP_CONFIG.TOKEN) {
      throw new Error('WhatsApp API Error: API token is not configured');
    }

    // Validate message length (WhatsApp limit is 4096 characters)
    if (text.length > 4096) {
      throw new Error('WhatsApp API Error: Message exceeds maximum length of 4096 characters');
    }

    try {
      // Clean up group ID format
      const cleanGroupId = groupId.replace('@g.us', '').trim();
      
      const response = await fetch(`${WHATSAPP_CONFIG.API_URL}`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({
          to: `${cleanGroupId}@g.us`,
          body: text,
          preview_url: previewUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error?.message || 
          errorData.message || 
          `WhatsApp API Error: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Add context to error
      const enhancedError = new Error(
        error instanceof Error ? error.message : 'Unknown WhatsApp API error'
      );
      
      // Add metadata
      Object.assign(enhancedError, {
        context: {
          groupId,
          textLength: text.length,
          hasToken: !!WHATSAPP_CONFIG.TOKEN,
          originalError: error
        }
      });

      throw enhancedError;
    }
  }

  static async sendToIndividual({ phone, text, previewUrl = false }: { phone: string; text: string; previewUrl?: boolean }): Promise<WhatsAppResponse> {
    // Validate required parameters
    if (!phone?.trim()) {
      throw new Error('WhatsApp API Error: phone number is required');
    }
    if (!text?.trim()) {
      throw new Error('WhatsApp API Error: text message is required');
    }
    if (!WHATSAPP_CONFIG.TOKEN) {
      throw new Error('WhatsApp API Error: API token is not configured');
    }

    // Validate message length (WhatsApp limit is 4096 characters)
    if (text.length > 4096) {
      throw new Error('WhatsApp API Error: Message exceeds maximum length of 4096 characters');
    }

    try {
      // Canonicalize to digits-only international format for WhatsApp
      const cleanPhone = toCanonicalPhone(phone);

      if (!cleanPhone || cleanPhone.length < 10 || !/^\d+$/.test(cleanPhone)) {
        throw new Error('WhatsApp API Error: Invalid phone number');
      }
      
      const response = await fetch(`${WHATSAPP_CONFIG.API_URL}`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({
          to: `${cleanPhone}@c.us`,
          body: text,
          preview_url: previewUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error?.message || 
          errorData.message || 
          `WhatsApp API Error: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Add context to error
      const enhancedError = new Error(
        error instanceof Error ? error.message : 'Unknown WhatsApp API error'
      );
      
      // Add metadata
      Object.assign(enhancedError, {
        context: {
          phone,
          textLength: text.length,
          hasToken: !!WHATSAPP_CONFIG.TOKEN,
          originalError: error
        }
      });

      throw enhancedError;
    }
  }

  static async searchArtistGroup(artistName: string): Promise<WhatsAppGroup | null> {
    if (!artistName?.trim()) {
      throw new Error('Artist name is required');
    }

    try {
      console.log(`🔍 Searching for artist group via Edge Function: "${artistName}"`);

      // Call the Edge Function instead of directly querying WHAPI
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search-artist-groups`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ artistName: artistName.trim() })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || 
          `Search failed: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      if (!data.success || !data.group) {
        console.log(`❌ No group found: ${data.message || 'Unknown error'}`);
        return null;
      }

      console.log(`✅ Found group: ${data.group.name}`);

      return {
        id: data.group.id,
        name: data.group.name,
        participants: data.group.participants,
        description: data.group.description
      };
    } catch (error) {
      const enhancedError = new Error(
        error instanceof Error ? error.message : 'Failed to search for artist group'
      );
      
      Object.assign(enhancedError, {
        context: {
          artistName,
          originalError: error
        }
      });

      console.error('Failed to search for artist group:', enhancedError);
      throw enhancedError;
    }
  }
}